<template>
  <div class="registrasi-surat-masuk-page">
    <div class="page-header">
      <h1>Registrasi Surat Masuk</h1>
    </div>

    <div class="content-container">
      <a-form :model="formData" layout="vertical" ref="formRef" @keydown.enter.prevent>
        <!-- Upload Section - Always show -->
        <a-form-item>
          <template #label>
            <span>Dokumen Surat <span style="color: #ff4d4f;">*</span></span>
          </template>
          <p class="file-hint">
            Mohon scan surat fisik terlebih dahulu dengan scanner agar mudah dibaca
          </p>

          <div
            class="upload-area"
            @click="triggerFileInput"
            @drop.prevent="handleFileDrop"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @dragenter.prevent
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              style="display: none"
              multiple
              @change="handleFileChange"
            />
            <div class="upload-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="36"
                height="36"
                viewBox="0 0 36 36"
                fill="none"
              >
                <path
                  d="M35.0457 14.6766L35.0363 14.6391L29.7769 1.275C29.5425 0.520312 28.8441 0 28.0519 0H6.73316C5.93629 0 5.22848 0.529688 5.00348 1.29375L0.0862889 14.5219L0.072227 14.5547L0.0628514 14.5922C0.00191392 14.8219 -0.0168361 15.0562 0.0159764 15.2859C0.0112889 15.3609 0.00660156 15.4359 0.00660156 15.5109V32.6625C0.00784108 33.418 0.308506 34.1422 0.842716 34.6764C1.37693 35.2106 2.10112 35.5113 2.8566 35.5125H32.2566C33.8269 35.5125 35.1066 34.2328 35.1113 32.6625V15.5109C35.1113 15.45 35.1113 15.3891 35.1066 15.3375C35.1254 15.1078 35.1066 14.8875 35.0457 14.6766ZM21.18 12.6609L21.166 13.3969C21.1285 15.5016 19.6754 16.9172 17.5519 16.9172C16.516 16.9172 15.6254 16.5844 14.9832 15.9516C14.341 15.3187 13.9894 14.4375 13.9707 13.3969L13.9566 12.6609H4.30973L8.03629 3.6H26.7488L30.5785 12.6609H21.18ZM3.60191 16.2609H10.9754C12.1144 18.9375 14.5379 20.5172 17.5566 20.5172C19.1363 20.5172 20.6035 20.0766 21.7894 19.2422C22.83 18.5109 23.641 17.4891 24.166 16.2609H31.5019V31.9125H3.60191V16.2609Z"
                  fill="#E10F28"
                />
              </svg>
            </div>
            <p class="upload-text">Klik atau seret file ke sini untuk mengunggah</p>
            <p class="upload-hint">Unggah file dalam format PDF, PNG, atau JPG/JPEG</p>
          </div>

          <!-- Uploaded Files List -->
          <div v-if="uploadedFiles.length > 0" class="uploaded-files-list">
            <div v-for="(file, index) in uploadedFiles" :key="file.id" class="uploaded-file-item">
              <PaperClipOutlined class="file-icon" />
              <span
                class="file-name"
                style="flex: 1; cursor: pointer;"
                @click="openFile(file)"
                :title="file.file.name"
                >{{ file.file.name }}</span
              >
              <button
                type="button"
                class="delete-button"
                @click.prevent="removeFile(index)"
                title="Hapus file"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path
                    d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
                  ></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </button>
            </div>
          </div>
        </a-form-item>

        <a-form-item v-if="uploadedFiles.length > 0">
          <div class="auto-detect-wrapper">
            <a-button
              class="auto-detect-button"
              size="large"
              @click="autoDetectWithAI"
              :disabled="uploadedFiles.length === 0 || isDetecting"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <mask
                  id="mask0_1239_4989"
                  style="mask-type: alpha"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="20"
                  height="20"
                >
                  <rect width="20" height="20" fill="#D9D9D9" />
                </mask>
                <g mask="url(#mask0_1239_4989)">
                  <path
                    d="M4.0625 17.021L3 15.9585L9.08333 9.87516L5.5 9.00016L9.375 6.5835L9.04167 2.021L12.5417 4.9585L16.75 3.25016L15.0417 7.47933L18 10.9585L13.4381 10.6252L11.0208 14.521L10.1458 10.9377L4.0625 17.021ZM4.5 7.00016L3 5.50016L4.5 4.00016L6 5.50016L4.5 7.00016ZM11.6042 10.6877L12.625 9.06266L14.5417 9.2085L13.306 7.746L14.0269 5.9585L12.2556 6.68766L10.7917 5.4585L10.9375 7.37516L9.29167 8.38329L11.1531 8.836L11.6042 10.6877ZM14.5 17.0002L13 15.5002L14.5 14.0002L16 15.5002L14.5 17.0002Z"
                    fill="currentColor"
                  />
                </g>
              </svg>
              {{ isDetecting ? 'Mendeteksi' : 'AUTO-DETEKSI ISI SURAT' }}
            </a-button>
            <a-tooltip placement="right">
              <template #title>
                <div style="max-width: 300px">
                  Fitur Auto-Deteksi dengan AI dapat mengisi field Nomor Surat, Tanggal Surat,
                  Pengirim, dan Perihal secara otomatis. Mohon upload file surat dalam format PDF
                  atau scan surat fisik sejauh mungkin.
                </div>
              </template>
              <question-circle-outlined class="info-icon" />
            </a-tooltip>
          </div>
        </a-form-item>

        <!-- Loading Modal for Auto Detect -->
        <div v-if="isDetecting">
          <a-divider />
          <div class="loading-modal">
            <div class="loading-spinner"></div>
            <h3 class="loading-title">Mohon tunggu</h3>
            <p class="loading-text">
              Sistem sedang membaca isi surat dan mengisi formulir registrasi surat masuk
            </p>
          </div>
        </div>
        <div v-else>
          <!-- Uploaded Files Detail Sections -->
          <div v-for="(file, index) in uploadedFiles" :key="file.id" class="uploaded-surat-section">
            <div class="surat-header">
              <h3>Surat {{ index + 1 }}</h3>
            </div>

            <div class="file-attachment">
              <PaperClipOutlined class="attachment-icon" />
              <a :href="getFilePreviewUrl(file.file)" target="_blank" class="file-link">
                {{ file.file.name }}
              </a>
            </div>

            <h4 class="section-title">Detail Surat</h4>

            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="validationErrors[index]?.waktuPenerimaan ? 'error' : ''"
                  :help="validationErrors[index]?.waktuPenerimaan"
                >
                  <template #label>
                    <span>Waktu Penerimaan Surat <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-date-picker
                    v-model:value="file.waktuPenerimaan"
                    placeholder="DD-MM-YYYY HH:mm"
                    size="large"
                    style="width: 100%"
                    format="DD-MM-YYYY HH:mm"
                    show-time
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="validationErrors[index]?.nomorSurat ? 'error' : ''"
                  :help="validationErrors[index]?.nomorSurat"
                >
                  <template #label>
                    <span>Nomor Surat <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-input
                    v-model:value="file.nomorSurat"
                    placeholder="Nomor surat"
                    size="large"
                    @keypress.space.prevent
                    @input="file.nomorSurat = file.nomorSurat.replace(/\s+/g, '')"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="validationErrors[index]?.tanggalSurat ? 'error' : ''"
                  :help="validationErrors[index]?.tanggalSurat"
                >
                  <template #label>
                    <span>Tanggal Surat <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-date-picker
                    v-model:value="file.tanggalSurat"
                    placeholder="DD-MM-YYYY"
                    size="large"
                    style="width: 100%"
                    format="DD-MM-YYYY"
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="validationErrors[index]?.pengirim ? 'error' : ''"
                  :help="validationErrors[index]?.pengirim"
                >
                  <template #label>
                    <span>Pengirim <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-input
                    v-model:value="file.pengirim"
                    placeholder="Orang atau lembaga pengirim surat"
                    size="large"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="16">
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item
                  :validate-status="validationErrors[index]?.perihal ? 'error' : ''"
                  :help="validationErrors[index]?.perihal"
                  style="margin-bottom: 0;"
                >
                  <template #label>
                    <span>Perihal <span style="color: #ff4d4f;">*</span></span>
                  </template>
                  <a-textarea
                    v-model:value="file.perihal"
                    placeholder="Perihal surat"
                    :rows="4"
                    size="large"
                  />
                </a-form-item>
              </a-col>
              <a-col :xs="24" :sm="24" :md="12" :lg="12">
                <a-form-item label="Keterangan" style="margin-bottom: 0;">
                  <a-textarea
                    v-model:value="file.keterangan"
                    placeholder="Keterangan atau catatan tambahan terkait surat"
                    :rows="4"
                    size="large"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <h4 class="section-title">Disposisi</h4>

            <a-form-item
              style="margin-bottom: 12px"
              :validate-status="validationErrors[index]?.disposisi ? 'error' : ''"
              :help="validationErrors[index]?.disposisi"
            >
              <template #label>
                <span>Disposisi Kepada <span style="color: #ff4d4f;">*</span></span>
              </template>
              <a-radio-group v-model:value="file.disposisi" size="large">
                <a-radio value="tidak didisposisi">Tidak Didisposisi</a-radio>
                <a-radio value="pekerja harian">Pekerja Harian</a-radio>
                <a-radio value="pengurus">Pengurus</a-radio>
                <a-radio value="penugasan dinas">Pengurus (Penugasan Dinas)</a-radio>
              </a-radio-group>
            </a-form-item>

            <!-- Task Cards Section - Show only if Pekerja Harian is selected -->
            <div v-if="file.disposisi === 'pekerja harian'" class="task-cards-section">
              <p class="task-info-text">
                Mengisi bagian ini akan membuat task baru pada aplikasi Lark secara otomatis
                berdasarkan data yang dimasukkan.
              </p>

              <!-- Task Cards -->
              <div v-for="(task, taskIndex) in file.tasks" :key="taskIndex" class="task-card">
                <a-row :gutter="16">
                  <a-col :xs="24" :sm="24" :md="12" :lg="12">
                    <a-form-item
                      :validate-status="
                        validationErrors[index]?.[`task_${taskIndex}_disposisi`] ? 'error' : ''
                      "
                      :help="validationErrors[index]?.[`task_${taskIndex}_disposisi`]"
                    >
                      <template #label>
                        <span>Disposisi Kepada <span style="color: #ff4d4f;">*</span></span>
                      </template>
                      <a-select
                        v-model:value="task.disposisi"
                        placeholder="Pilih satu atau lebih pekerja harian"
                        mode="multiple"
                        size="large"
                        show-search
                        :options="pekerjaHarianOptions"
                        :filter-option="filterOption"
                      >
                      </a-select>
                    </a-form-item>
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="12" :lg="12">
                    <a-form-item
                      :validate-status="
                        validationErrors[index]?.[`task_${taskIndex}_judul`] ? 'error' : ''
                      "
                      :help="validationErrors[index]?.[`task_${taskIndex}_judul`]"
                    >
                      <template #label>
                        <span>Judul Task <span style="color: #ff4d4f;">*</span></span>
                      </template>
                      <a-input
                        v-model:value="task.judul"
                        placeholder="Judul singkat task"
                        size="large"
                      />
                    </a-form-item>
                  </a-col>
                </a-row>

                <a-row :gutter="16">
                  <a-col :xs="24" :sm="24" :md="12" :lg="12">
                    <a-form-item label="Deadline Task">
                      <a-date-picker
                        v-model:value="task.deadline"
                        placeholder="DD-MM-YYYY"
                        size="large"
                        style="width: 100%"
                        format="DD-MM-YYYY"
                        :disabled-date="disabledPastDates"
                      />
                    </a-form-item>
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="12" :lg="12">
                    <a-form-item label="Deskripsi Task">
                      <a-textarea
                        v-model:value="task.deskripsi"
                        placeholder="Deskripsi task yang perlu dilakukan oleh pekerja harian"
                        :rows="4"
                        size="large"
                      />
                    </a-form-item>
                  </a-col>
                </a-row>

                <!-- Hapus Task Button - Show only if more than one task -->
                <a-form-item v-if="file.tasks.length > 1">
                  <button class="hapus-task-button" @click.prevent="removeTask(index, taskIndex)">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7V16C5 16.5304 5.21071 17.0391 5.58579 17.4142C5.96086 17.7893 6.46957 18 7 18H13C13.5304 18 14.0391 17.7893 14.4142 17.4142C14.7893 17.0391 15 16.5304 15 16V7M16 5H4M8 3H12"
                        stroke="white"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    HAPUS TASK DISPOSISI
                  </button>
                </a-form-item>
              </div>

              <!-- Tambah Task Button -->
              <a-button type="link" class="tambah-task-button" @click="addTask(index)">
                <plus-outlined />
                <span class="font-button">TAMBAH TASK DISPOSISI</span>
              </a-button>
            </div>

            <!-- Pengurus Disposition Section - Show only if Pengurus is selected -->
            <div v-if="file.disposisi === 'pengurus'" class="pengurus-cards-section">
              <p class="pengurus-info-text">
                Mengisi bagian ini akan membuat disposisi baru untuk pengurus yang dituju.
              </p>

              <!-- Pengurus Cards -->
              <div
                v-for="(pengurus, pengurusIndex) in file.pengurusDispositions"
                :key="pengurusIndex"
                class="pengurus-card"
              >
                <a-row :gutter="16">
                  <a-col :xs="24" :sm="24" :md="12" :lg="12">
                    <a-form-item
                      :validate-status="
                        validationErrors[index]?.[`pengurus_${pengurusIndex}_disposisi`]
                          ? 'error'
                          : ''
                      "
                      :help="validationErrors[index]?.[`pengurus_${pengurusIndex}_disposisi`]"
                    >
                      <template #label>
                        <span>Disposisi Kepada <span style="color: #ff4d4f;">*</span></span>
                      </template>
                      <a-select
                        v-model:value="pengurus.disposisi"
                        placeholder="Pilih pengurus tujuan disposisi"
                        size="large"
                        show-search
                        :options="pengurusOptions"
                        :filter-option="filterOption"
                      >
                      </a-select>
                    </a-form-item>
                  </a-col>
                  <a-col :xs="24" :sm="24" :md="12" :lg="12">
                    <a-form-item
                      :validate-status="
                        validationErrors[index]?.[`pengurus_${pengurusIndex}_perihal`]
                          ? 'error'
                          : ''
                      "
                      :help="validationErrors[index]?.[`pengurus_${pengurusIndex}_perihal`]"
                    >
                      <template #label>
                        <span>Perihal Instruksi <span style="color: #ff4d4f;">*</span></span>
                      </template>
                      <a-input
                        v-model:value="pengurus.perihal"
                        placeholder="Perihal atau judul singkat instruksi"
                        size="large"
                      />
                    </a-form-item>
                  </a-col>
                </a-row>

                <a-form-item label="Instruksi Disposisi">
                  <a-textarea
                    v-model:value="pengurus.instruksi"
                    placeholder="Deskripsi hal atau instruksi yang perlu dilakukan oleh pengurus"
                    :rows="4"
                    size="large"
                  />
                </a-form-item>

                <!-- Hapus Tujuan Disposisi Button - Show only if more than one disposition -->
                <a-form-item v-if="file.pengurusDispositions.length > 1">
                  <button
                    class="hapus-disposisi-button"
                    @click.prevent="removePengurusDisposition(index, pengurusIndex)"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7V16C5 16.5304 5.21071 17.0391 5.58579 17.4142C5.96086 17.7893 6.46957 18 7 18H13C13.5304 18 14.0391 17.7893 14.4142 17.4142C14.7893 17.0391 15 16.5304 15 16V7M16 5H4M8 3H12"
                        stroke="white"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    HAPUS DISPOSISI
                  </button>
                </a-form-item>
              </div>

              <!-- Tambah Tujuan Disposisi Button -->
              <a-button
                type="link"
                class="tambah-disposisi-button"
                @click="addPengurusDisposition(index)"
              >
                <plus-outlined />
                TAMBAH TUJUAN DISPOSISI
              </a-button>
            </div>

            <!-- Penugasan Dinas Section - Show only if Pengurus (Penugasan Dinas) is selected -->
            <div v-if="file.disposisi === 'penugasan dinas'" class="penugasan-dinas-section">
              <p class="penugasan-info-text">
                Mengisi bagian ini akan membuat surat keluar baru berupa surat tugas secara otomatis
                berdasarkan data yang dimasukkan. Surat tugas akan dikirimkan ke verifikator atau
                penandatangan yang dipilih.
              </p>

              <!-- Nomor Surat Section -->
              <div class="nomor-surat-input">
                <label class="section-label"
                  >Nomor Surat <span style="color: #ff4d4f">*</span></label
                >
                <p class="section-description">
                  Apabila nomor surat sudah digunakan oleh surat yang dibuat di luar sistem, gunakan
                  tombol tambah (+) untuk menaikkan nomor surat
                </p>
                <div class="input-wrapper">
                  <a-input
                    v-model:value="file.penugasanDinas.nomorSurat"
                    placeholder="Nomor surat tugas"
                    disabled
                    size="large"
                    class="nomor-input"
                  />
                  <a-button
                    type="text"
                    class="btn-action"
                    @click.prevent="incrementNomorSurat(file.id)"
                  >
                    <PlusOutlined />
                  </a-button>
                  <a-button
                    type="text"
                    class="btn-action"
                    @click.prevent="decrementNomorSurat(file.id)"
                    :disabled="!canDecrementNomorSurat(file.id)"
                  >
                    <MinusOutlined />
                  </a-button>
                </div>
                <span v-if="validationErrors[index]?.pd_nomorSurat" class="error-hint">
                  {{ validationErrors[index].pd_nomorSurat }}
                </span>
              </div>

              <!-- Sifat Surat Section -->
              <a-form-item
                :validate-status="validationErrors[index]?.pd_sifatSurat ? 'error' : ''"
                :help="validationErrors[index]?.pd_sifatSurat"
              >
                <template #label>
                  <span>Sifat Surat <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-radio-group v-model:value="file.penugasanDinas.sifatSurat" size="large">
                  <a-radio value="biasa">Biasa</a-radio>
                  <a-radio value="terbatas">Terbatas</a-radio>
                  <a-radio value="rahasia">Rahasia</a-radio>
                  <a-radio value="sangat rahasia">Sangat Rahasia</a-radio>
                </a-radio-group>
              </a-form-item>

              <!-- Bentuk Surat Section -->
              <a-form-item
                :validate-status="validationErrors[index]?.pd_bentukSurat ? 'error' : ''"
                :help="validationErrors[index]?.pd_bentukSurat"
              >
                <template #label>
                  <span>Bentuk Surat <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-radio-group v-model:value="file.penugasanDinas.bentukSurat">
                  <div class="bentuk-surat-option">
                    <a-radio value="Soft copy">Soft copy</a-radio>
                    <p class="bentuk-surat-desc">
                      Menggunakan tanda tangan dan stempel digital. Surat dapat diunduh di website
                      ini (format PDF).
                    </p>
                  </div>
                  <div class="bentuk-surat-option">
                    <a-radio value="Hard copy">Hard copy</a-radio>
                    <p class="bentuk-surat-desc">
                      Menggunakan tanda tangan dan stempel basah. Surat dapat diambil di Sekretariat
                      PERKI.
                    </p>
                  </div>
                </a-radio-group>
              </a-form-item>

              <!-- Main Details Row -->
              <a-row :gutter="16">
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    :validate-status="validationErrors[index]?.pd_namaKegiatan ? 'error' : ''"
                    :help="validationErrors[index]?.pd_namaKegiatan"
                  >
                    <template #label>
                      <span>Nama Kegiatan <span style="color: #ff4d4f;">*</span></span>
                    </template>
                    <a-input
                      v-model:value="file.penugasanDinas.namaKegiatan"
                      placeholder="Nama kegiatan dinas"
                      size="large"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    :validate-status="validationErrors[index]?.pd_waktuPerjalanan ? 'error' : ''"
                    :help="validationErrors[index]?.pd_waktuPerjalanan"
                  >
                    <template #label>
                      <span>Waktu Perjalanan <span style="color: #ff4d4f;">*</span></span>
                    </template>
                    <a-date-picker
                      v-model:value="file.penugasanDinas.waktuPerjalanan"
                      placeholder="DD-MM-YYYY HH:MM"
                      size="large"
                      style="width: 100%"
                      format="DD-MM-YYYY HH:mm"
                      show-time
                    />
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Location and Date Row -->
              <a-row :gutter="16">
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    :validate-status="validationErrors[index]?.pd_tempatKegiatan ? 'error' : ''"
                    :help="validationErrors[index]?.pd_tempatKegiatan"
                  >
                    <template #label>
                      <span>Tempat Kegiatan <span style="color: #ff4d4f;">*</span></span>
                    </template>
                    <a-input
                      v-model:value="file.penugasanDinas.tempatKegiatan"
                      placeholder="Tempat kegiatan dinas"
                      size="large"
                    />
                  </a-form-item>
                </a-col>
                <a-col :xs="24" :sm="24" :md="12" :lg="12">
                  <a-form-item
                    :validate-status="validationErrors[index]?.pd_tanggalSurat ? 'error' : ''"
                    :help="validationErrors[index]?.pd_tanggalSurat"
                  >
                    <template #label>
                      <span>Tanggal Surat <span style="color: #ff4d4f;">*</span></span>
                    </template>
                    <a-date-picker
                      v-model:value="file.penugasanDinas.tanggalSurat"
                      placeholder="DD-MM-YYYY"
                      size="large"
                      style="width: 100%"
                      format="DD-MM-YYYY"
                    />
                  </a-form-item>
                </a-col>
              </a-row>

              <!-- Penandatangan Section -->
              <PenandatanganSelect
                v-model:kiri-value="file.penugasanDinas.penandatanganKiriId"
                v-model:kanan-value="file.penugasanDinas.penandatanganKananId"
                :kiri-error="validationErrors[index]?.pd_penandatanganKiri"
                :kanan-error="validationErrors[index]?.pd_penandatanganKanan"
              />

              <!-- Keperluan Section -->
              <a-form-item
                :validate-status="validationErrors[index]?.pd_keperluan ? 'error' : ''"
                :help="validationErrors[index]?.pd_keperluan"
              >
                <template #label>
                  <span>Keperluan <span style="color: #ff4d4f;">*</span></span>
                </template>
                <a-textarea
                  v-model:value="file.penugasanDinas.keperluan"
                  placeholder="Keperluan dinas"
                  :rows="3"
                  size="large"
                />
              </a-form-item>

              <!-- Perwakilan Dinas Table -->
              <PerwakilanDinasTable
                :data-source="file.penugasanDinas.perwakilan"
                :show-pdf-button="false"
                :error="validationErrors[index]?.pd_perwakilan"
                @add="() => addPerwakilanDinas(index)"
                @remove="rowIndex => removePerwakilanDinas(index, rowIndex)"
                @preview="({ record }) => handlePreviewPerwakilan(index, record)"
              />

              <!-- Verifikator Section -->
              <VerifikatorTable
                :data-source="file.penugasanDinas.verifikator"
                @add="() => addVerifikator(index)"
                @remove="rowIndex => removeVerifikator(index, rowIndex)"
              />

              <AlurVerifikasi :verifikator="file.penugasanDinas.verifikator" />
            </div>

            <a-form-item>
              <button class="hapus-surat-button" @click.prevent="removeFile(index)">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7V16C5 16.5304 5.21071 17.0391 5.58579 17.4142C5.96086 17.7893 6.46957 18 7 18H13C13.5304 18 14.0391 17.7893 14.4142 17.4142C14.7893 17.0391 15 16.5304 15 16V7M16 5H4M8 3H12"
                    stroke="white"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
                HAPUS SURAT
              </button>
            </a-form-item>
          </div>
        </div>

        <a-divider v-if="!isDetecting" />

        <a-form-item>
          <button
            type="button"
            @click="handleSubmit"
            class="confirm-button"
            :disabled="submitting || uploadedFiles.length === 0"
          >
            <span v-if="submitting" class="button-spinner"></span>
            {{ submitting ? 'MENYIMPAN...' : 'SIMPAN' }}
          </button>
        </a-form-item>
      </a-form>
    </div>

    <!-- Floating Scroll to Top Button -->
    <transition name="fade">
      <button
        v-show="showScrollTop"
        class="scroll-to-top-btn"
        @click="scrollToTop"
        title="Kembali ke atas"
      >
        <ArrowUpOutlined />
      </button>
    </transition>

    <!-- Submit Confirmation Modal -->
    <a-modal
      v-model:visible="submitConfirmModalVisible"
      :footer="null"
      :closable="false"
      centered
      width="480px"
      class="submit-confirm-modal"
    >
      <div class="confirm-modal-content">
        <h3 class="confirm-modal-title">Yakin isi formulir sudah sesuai?</h3>
        <p class="confirm-modal-description">
          Mohon cek kembali data surat sebelum menyimpan formulir. Apabila bagian Disposisi diisi,
          sistem akan membuat task/lark/disposisi/surat tugas baru sesuai jenis disposisi yang
          dipilih.
        </p>
        <div class="confirm-modal-actions">
          <button class="modal-cancel-button" @click="submitConfirmModalVisible = false">
            KEMBALI
          </button>
          <button class="modal-confirm-button" :disabled="submitting" @click="confirmSubmit">
            YA, SIMPAN
          </button>
        </div>
      </div>
    </a-modal>

    <!-- PDF Preview Modal -->
    <PDFPreviewModal
      v-model:visible="pdfPreviewVisible"
      :title="pdfPreviewTitle"
      :pdf-path="pdfPreviewPath"
    />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { message, notification } from 'ant-design-vue'
import {
  LeftOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
  MinusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons-vue'
import {
  suratMasukAPI,
  suratKeluarAPI,
  uploadFilesAPI,
  userAPI,
  documentExtractionAPI,
} from '@/services/axios/perki'
import dayjs from 'dayjs'

import {
  VerifikatorTable,
  AlurVerifikasi,
  PenandatanganSelect,
  PerwakilanDinasTable,
  PDFPreviewModal,
} from '@/components/SuratKeluar'

import { useNomorSurat } from '@/composables'
import { useDateFormatting } from '@/composables'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'

export default {
  name: 'RegistrasiSuratMasuk',
  components: {
    PaperClipOutlined,
    QuestionCircleOutlined,
    PlusOutlined,
    MinusOutlined,
    ArrowUpOutlined,
    VerifikatorTable,
    AlurVerifikasi,
    PenandatanganSelect,
    PerwakilanDinasTable,
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const store = useStore()
    const formRef = ref(null)
    const fileInput = ref(null)
    const uploadedFiles = ref([])
    const submitting = ref(false)
    const isDetecting = ref(false)
    const validationErrors = ref({})
    let fileIdCounter = 0

    // Submit confirmation modal state
    const submitConfirmModalVisible = ref(false)

    // Scroll to top state
    const showScrollTop = ref(false)
    const scrollThreshold = 1300

    // PDF Preview Modal state
    const pdfPreviewVisible = ref(false)
    const pdfPreviewTitle = ref('')
    const pdfPreviewPath = ref('')

    // Nomor surat state for each file's penugasan dinas
    const nomorSuratState = ref(new Map()) // Map<fileId, { baseNumber, canDecrement }>

    const { formatForAPI, formatDateTimeForAPI } = useDateFormatting()

    const userGroupId = localStorage.getItem('perki_group_id')

    // Unsaved changes warning
    const hasFormChanges = computed(() => {
      return uploadedFiles.value.length > 0 || 
        formData.files.some(file => 
          file.nomorSurat || 
          file.tanggalSurat || 
          file.sifatSurat !== 'Biasa' ||
          file.bentukSurat !== 'Soft copy' ||
          file.perihal ||
          file.asal ||
          file.tempatKegiatan ||
          file.namaKegiatan ||
          file.waktuPerjalanan ||
          file.penandatanganId ||
          file.penandatanganKananId ||
          file.keperluan ||
          (file.perwakilanDinas && file.perwakilanDinas.length > 0) ||
          (file.verifikator && file.verifikator.length > 0) ||
          (file.disposisi && file.disposisi.length > 0),
        )
    })
    const { setHasUnsavedChanges, resetUnsavedChanges } = useUnsavedChangesWarning({ hasFormChanges })

    // Undangan Acara options
    const undanganAcaraOptions = computed(() => store.state.suratKeluar.undanganAcaraOptions)

    // Use nomor surat composable for generation logic
    const {
      nomorSurat: nomorSuratRef,
      canDecrement,
      fetchLatestNomorSurat,
      incrementNomorSurat: incrementNomorSuratComposable,
      decrementNomorSurat: decrementNomorSuratComposable,
      generateNomorSurat,
      extractNumberFromNomorSurat,
      resetNomorSurat,
    } = useNomorSurat()

    const formData = reactive({
      files: [],
    })

    // Table columns for Perwakilan Dinas
    const perwakilanColumns = [
      { title: 'Nama Pengurus', dataIndex: 'namaPengurus', key: 'namaPengurus', width: '35%' },
      { title: 'Jabatan', dataIndex: 'jabatan', key: 'jabatan', width: '35%' },
      { title: 'NPA PERKI', dataIndex: 'npaPERKI', key: 'npaPERKI', width: '20%' },
      { title: 'Aksi', key: 'aksi', width: '10%', align: 'center' },
    ]

    // Table columns for Verifikator
    const verifikatorColumns = [
      { title: 'No. Urut', dataIndex: 'noUrut', key: 'noUrut', width: '10%' },
      { title: 'Nama Pengurus', dataIndex: 'namaPengurus', key: 'namaPengurus', width: '30%' },
      { title: 'Jabatan', dataIndex: 'jabatan', key: 'jabatan', width: '30%' },
      { title: 'NPA PERKI', dataIndex: 'npaPERKI', key: 'npaPERKI', width: '20%' },
      { title: 'Aksi', key: 'aksi', width: '10%', align: 'center' },
    ]

    // Pengurus options for penugasan dinas (combines store options)
    const pengurusOptions = ref([])

    // Pekerja harian options for regular disposisi
    const pekerjaHarianOptions = ref([])

    // Filter option function for searchable selects
    const filterOption = (input, option) => {
      return option.label.toLowerCase().includes(input.toLowerCase())
    }

    // Disable past dates for deadline task
    const disabledPastDates = current => {
      // Disable dates before today
      return current && current < dayjs().startOf('day')
    }

    // Fetch pekerja harian users separately
    const fetchGroupUsers = async () => {
      try {
        const response = await userAPI.getUsersInMyGroup({ limit: 1000 })
        if (response.data?.data?.users) {
          const users = response.data.data.users

          // Filter and format pengurus (excluding pekerja_harian)
          const pengurusList = users
            .filter(user => user.role !== 'pekerja_harian' && user.role !== 'admin')
            .map(user => ({
              label: `${user.name} (${user.jabatan})`,
              value: user.id,
            }))

          // Filter and format pekerja harian
          const pekerjaList = users
            .filter(user => user.role === 'pekerja_harian')
            .map(user => ({
              label: user.name,
              value: user.id,
            }))

          pengurusOptions.value = pengurusList
          pekerjaHarianOptions.value = pekerjaList
        }
      } catch (error) {
        console.error('Error fetching pekerja harian users:', error)
        notification.error({ message: 'Error', description: 'Gagal memuat daftar pekerja harian' })
      }
    }

    // Initialize data on component mount
    onMounted(async () => {
      // Fetch pengurus options from store for penugasan dinas
      await store.dispatch('suratKeluar/fetchPengurusOptions')

      // Fetch pekerja harian for regular disposisi
      if (userGroupId) {
        await fetchGroupUsers()
      }

      // Fetch latest nomor surat for penugasan dinas
      await fetchLatestNomorSurat()

      // Add scroll event listener
      window.addEventListener('scroll', handleScroll)
    })

    onUnmounted(() => {
      // Remove scroll event listener
      window.removeEventListener('scroll', handleScroll)
    })

    const triggerFileInput = () => {
      fileInput.value.click()
    }

    const handleFileChange = event => {
      const files = Array.from(event.target.files)
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      files.forEach(file => {
        if (!validTypes.includes(file.type)) {
          notification.error({
            message: 'Error',
            description: `${file.name}: Format file tidak didukung. Gunakan PDF, PNG, atau JPG/JPEG`,
          })
          return
        }

        if (file.size > maxSize) {
          notification.error({
            message: 'Error',
            description: `${file.name}: Ukuran file terlalu besar. Maksimal 10MB`,
          })
          return
        }

        const fileData = {
          id: fileIdCounter++,
          file: file,
          nomorSurat: '',
          tanggalSurat: null,
          pengirim: '',
          waktuPenerimaan: dayjs(),
          perihal: '',
          keterangan: '',
          lampiran: '',
          disposisi: '',
          tasks: [
            {
              disposisi: [],
              judul: '',
              deadline: null,
              deskripsi: '',
            },
          ],
          pengurusDispositions: [
            {
              disposisi: null,
              perihal: '',
              instruksi: '',
            },
          ],
          penugasanDinas: {
            nomorSurat: nomorSuratRef.value,
            sifatSurat: 'biasa',
            bentukSurat: 'Soft copy',
            namaKegiatan: '',
            waktuPerjalanan: null,
            tempatKegiatan: '',
            tanggalSurat: null,
            penandatanganKiriId: null,
            penandatanganKananId: null,
            keperluan: '',
            perwakilan: [
              {
                userId: null,
                searchValue: '',
                isNonPengurus: false,
                namaNonPengurus: null,
                jabatanNonPengurus: null,
                npaNonPengurus: null,
              },
            ],
            verifikator: [
              {
                userId: null,
                urutan: 1,
              },
            ],
          },
        }

        uploadedFiles.value.push(fileData)
        notification.success({
          message: 'Success',
          description: `File ${file.name} berhasil diunggah`,
        })
      })

      // Reset input
      event.target.value = ''
    }

    const handleFileDrop = event => {
      const files = Array.from(event.dataTransfer.files)
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      files.forEach(file => {
        if (!validTypes.includes(file.type)) {
          notification.error({
            message: 'Error',
            description: `${file.name}: Format file tidak didukung. Gunakan PDF, PNG, atau JPG/JPEG`,
          })
          return
        }

        if (file.size > maxSize) {
          notification.error({
            message: 'Error',
            description: `${file.name}: Ukuran file terlalu besar. Maksimal 10MB`,
          })
          return
        }

        const fileData = {
          id: fileIdCounter++,
          file: file,
          nomorSurat: '',
          tanggalSurat: null,
          pengirim: '',
          waktuPenerimaan: dayjs(),
          perihal: '',
          keterangan: '',
          lampiran: '',
          disposisi: '',
          tasks: [
            {
              disposisi: [],
              judul: '',
              deadline: null,
              deskripsi: '',
            },
          ],
          pengurusDispositions: [
            {
              disposisi: null,
              perihal: '',
              instruksi: '',
            },
          ],
          penugasanDinas: {
            nomorSurat: nomorSuratRef.value,
            sifatSurat: 'biasa',
            bentukSurat: 'Soft copy',
            namaKegiatan: '',
            waktuPerjalanan: null,
            tempatKegiatan: '',
            tanggalSurat: null,
            penandatanganKiriId: null,
            penandatanganKananId: null,
            keperluan: '',
            perwakilan: [
              {
                userId: null,
                searchValue: '',
                isNonPengurus: false,
                namaNonPengurus: null,
                jabatanNonPengurus: null,
                npaNonPengurus: null,
              },
            ],
            verifikator: [
              {
                userId: null,
                urutan: 1,
              },
            ],
          },
        }

        uploadedFiles.value.push(fileData)
        notification.success({
          message: 'Success',
          description: `File ${file.name} berhasil diunggah`,
        })
      })
    }

    const handleDragOver = event => {
      event.currentTarget.classList.add('drag-over')
    }

    const handleDragLeave = event => {
      event.currentTarget.classList.remove('drag-over')
    }

    const openFile = file => {
      // Create a blob URL from the file and open in new tab
      const fileUrl = URL.createObjectURL(file.file)
      window.open(fileUrl, '_blank')
      // Optional: cleanup the URL after a delay
      setTimeout(() => {
        URL.revokeObjectURL(fileUrl)
      }, 1000)
    }

    const removeFile = index => {
      const fileName = uploadedFiles.value[index].file.name
      uploadedFiles.value.splice(index, 1)
    }

    const addTask = index => {
      uploadedFiles.value[index].tasks.push({
        disposisi: [],
        judul: '',
        deadline: null,
        deskripsi: '',
      })
    }

    const removeTask = (fileIndex, taskIndex) => {
      uploadedFiles.value[fileIndex].tasks.splice(taskIndex, 1)
    }

    const addPengurusDisposition = index => {
      uploadedFiles.value[index].pengurusDispositions.push({
        disposisi: null,
        perihal: '',
        instruksi: '',
      })
    }

    const removePengurusDisposition = (fileIndex, dispositionIndex) => {
      uploadedFiles.value[fileIndex].pengurusDispositions.splice(dispositionIndex, 1)
    }

    const addPerwakilanDinas = index => {
      uploadedFiles.value[index].penugasanDinas.perwakilan.push({
        userId: null,
        searchValue: '',
        isNonPengurus: false,
        namaNonPengurus: null,
        jabatanNonPengurus: null,
        npaNonPengurus: null,
      })
    }

    const removePerwakilanDinas = (fileIndex, rowIndex) => {
      uploadedFiles.value[fileIndex].penugasanDinas.perwakilan.splice(rowIndex, 1)
    }

    // Handle preview for Perwakilan Dinas row
    const handlePreviewPerwakilan = async (fileIndex, record) => {
      const file = uploadedFiles.value[fileIndex]
      if (!file) return

      // Check if record has either userId (pengurus) or non-pengurus data
      if (!record.userId && !record.isNonPengurus) {
        notification.warning({ message: 'Silakan pilih perwakilan dinas terlebih dahulu' })
        return
      }

      // For non-pengurus, require all fields
      if (record.isNonPengurus) {
        if (!record.namaNonPengurus || !record.jabatanNonPengurus || !record.npaNonPengurus) {
          notification.warning({
            message: 'Data tidak lengkap',
            description: 'Lengkapi Nama, Jabatan, dan NPA untuk non-pengurus',
          })
          return
        }
      }

      // Minimal validation - only require perwakilan to be selected
      if (!file.penugasanDinas.namaKegiatan) {
        notification.warning({
          message: 'Nama Kegiatan diperlukan',
          description: 'Minimal isi Nama Kegiatan untuk melihat preview surat',
        })
        return
      }

      try {
        // Prepare preview data - send whatever is currently filled
        const pd = file.penugasanDinas
        const previewData = {
          jenisSurat: 'Surat Tugas',
          nomorSurat: pd.nomorSurat || '[NOMOR BELUM DIISI]',
          sifatSurat: pd.sifatSurat || 'Biasa',
          bentukSurat: pd.bentukSurat || 'Soft Copy',
          tanggalSurat: pd.tanggalSurat ? pd.tanggalSurat.format('DD-MM-YYYY') : null,
          namaKegiatan: pd.namaKegiatan,
          waktuPerjalanan: formatDateTimeForAPI(pd.waktuPerjalanan),
          tempatKegiatan: pd.tempatKegiatan || null,
          keperluan: pd.keperluan || null,
          penandatanganKiriId: pd.penandatanganKiriId || null,
          penandatanganKananId: pd.penandatanganKananId || null,
        }

        // Add perwakilan data based on type (pengurus or non-pengurus)
        if (record.isNonPengurus) {
          previewData.isNonPengurus = true
          previewData.namaNonPengurus = record.namaNonPengurus
          previewData.jabatanNonPengurus = record.jabatanNonPengurus
          previewData.npaNonPengurus = record.npaNonPengurus
        } else {
          previewData.perwakilanDinasUserId = record.userId
        }

        // Call preview API
        const response = await suratKeluarAPI.previewPerwakilanDinasPDF(previewData)

        // Create blob URL for modal preview
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)

        // Get perwakilan name for modal title
        let perwakilanName = 'Perwakilan'
        if (record.isNonPengurus) {
          perwakilanName = record.namaNonPengurus || 'Non-Pengurus'
        } else {
          const pengurusMap = store.state.suratKeluar.pengurusMap || {}
          const perwakilan = pengurusMap[record.userId]
          perwakilanName = perwakilan?.name || 'Perwakilan'
        }

        pdfPreviewTitle.value = `Preview Surat Tugas - ${perwakilanName}`
        pdfPreviewPath.value = blobUrl
        pdfPreviewVisible.value = true

        // Clean up blob URL after modal is closed (5 seconds after opening)
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000)
      } catch (error) {
        console.error('Preview error:', error)
        notification.error({
          message: 'Gagal membuat preview',
          description:
            error.response?.data?.message || 'Terjadi kesalahan saat membuat preview PDF',
        })
      }
    }

    const addVerifikator = index => {
      const nextUrutan = uploadedFiles.value[index].penugasanDinas.verifikator.length + 1
      uploadedFiles.value[index].penugasanDinas.verifikator.push({
        userId: null,
        urutan: nextUrutan,
      })
    }

    const removeVerifikator = (fileIndex, rowIndex) => {
      uploadedFiles.value[fileIndex].penugasanDinas.verifikator.splice(rowIndex, 1)
      // Reorder urutan
      uploadedFiles.value[fileIndex].penugasanDinas.verifikator.forEach((item, idx) => {
        item.urutan = idx + 1
      })
    }

    // Helper functions for pengurus data using store getters
    const getPengurusJabatan = userId => {
      if (!userId) return ''
      const user = pengurusMap.value[userId]
      return user?.jabatan || ''
    }

    const getPengurusNPA = userId => {
      if (!userId) return ''
      const user = pengurusMap.value[userId]
      return user?.npa || ''
    }

    const getPengurusName = userId => {
      if (!userId) return ''
      const user = pengurusMap.value[userId]
      return user?.name || ''
    }

    const filterPengurusOption = (input, option) => {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    // Nomor surat increment/decrement functions
    const incrementNomorSurat = fileId => {
      const file = uploadedFiles.value.find(f => f.id === fileId)
      if (!file) return

      const currentNomor = file.penugasanDinas.nomorSurat
      const currentNumber = currentNomor ? extractNumberFromNomorSurat(currentNomor) : 0
      const newNumber = currentNumber + 1

      file.penugasanDinas.nomorSurat = generateNomorSurat(newNumber)

      // Update state
      const state = nomorSuratState.value.get(fileId) || {
        baseNumber: 0,
        initialNumber: extractNumberFromNomorSurat(nomorSuratRef.value),
        canDecrement: false,
      }
      state.baseNumber = newNumber
      state.canDecrement = newNumber > state.initialNumber
      nomorSuratState.value.set(fileId, state)
    }

    const decrementNomorSurat = fileId => {
      const file = uploadedFiles.value.find(f => f.id === fileId)
      if (!file) return

      const state = nomorSuratState.value.get(fileId)
      if (!state || !state.canDecrement) return

      const currentNomor = file.penugasanDinas.nomorSurat
      const currentNumber = currentNomor
        ? extractNumberFromNomorSurat(currentNomor)
        : state.initialNumber

      // Can only decrement down to the initial number
      if (currentNumber > state.initialNumber) {
        const newNumber = currentNumber - 1
        file.penugasanDinas.nomorSurat = generateNomorSurat(newNumber)

        state.baseNumber = newNumber
        // Disable decrement when we reach the initial number
        state.canDecrement = newNumber > state.initialNumber
        nomorSuratState.value.set(fileId, state)
      }
    }

    const canDecrementNomorSurat = fileId => {
      const state = nomorSuratState.value.get(fileId)
      return state?.canDecrement || false
    }

    const previewSuratTugas = index => {
      // Implement preview functionality here
    }

    const formatFileSize = bytes => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    }

    const autoDetectWithAI = async () => {
      if (uploadedFiles.value.length === 0) {
        notification.warning({
          message: 'Warning',
          description: 'Mohon upload file terlebih dahulu',
        })
        return
      }

      isDetecting.value = true
      let successCount = 0
      let errorCount = 0
      const allMissingFields = []

      try {
        // Process each file for AI extraction via backend
        for (let i = 0; i < uploadedFiles.value.length; i++) {
          const fileData = uploadedFiles.value[i]

          try {
            // Create FormData for the file
            const formData = new FormData()
            formData.append('file', fileData.file)

            // Call backend API to extract document info (backend calls OpenAI)
            const response = await documentExtractionAPI.extractFromFile(formData)

            if (response.data && response.data.data) {
              const extractedData = response.data.data
              const missingFields = []

              // Map extracted data to form fields and track missing ones
              if (extractedData.no_surat && extractedData.no_surat.trim() !== '') {
                fileData.nomorSurat = extractedData.no_surat
              } else {
                missingFields.push('Nomor Surat')
              }

              if (extractedData.tanggal_surat && extractedData.tanggal_surat.trim() !== '') {
                fileData.tanggalSurat = dayjs(extractedData.tanggal_surat)
              } else {
                missingFields.push('Tanggal Surat')
              }

              if (extractedData.pengirim && extractedData.pengirim.trim() !== '') {
                fileData.pengirim = extractedData.pengirim
              } else {
                missingFields.push('Pengirim')
              }

              if (extractedData.perihal && extractedData.perihal.trim() !== '') {
                fileData.perihal = extractedData.perihal
              } else {
                missingFields.push('Perihal')
              }

              // Store missing fields for this file
              if (missingFields.length > 0) {
                allMissingFields.push({
                  fileName: fileData.file.name,
                  fields: missingFields,
                })
              }

              successCount++
            }
          } catch (fileError) {
            console.error(`Error extracting file ${fileData.file.name}:`, fileError)
            errorCount++
          }
        }

        // Show result message with details about missing fields
        if (successCount > 0 && errorCount === 0) {
          if (allMissingFields.length === 0) {
            // All fields retrieved successfully for all files
            notification.success({
              message: 'Success',
              description: `Deteksi otomatis berhasil! Semua data telah terisi lengkap untuk ${successCount} surat.`,
            })
          } else {
            notification.warning({
              message: 'Warning',
              description: `Deteksi berhasil untuk ${successCount} surat, namun beberapa field tidak terdeteksi, mohon cek kembali.`,
            })
          }
        } else if (successCount > 0 && errorCount > 0) {
          notification.warning({
            message: 'Warning',
            description: `Deteksi berhasil untuk ${successCount} surat, ${errorCount} surat gagal.`,
          })
        } else {
          notification.error({
            message: 'Error',
            description: 'Gagal melakukan deteksi otomatis untuk semua surat',
          })
        }
      } catch (error) {
        console.error('Error auto-detecting:', error)
        notification.error({ message: 'Error', description: 'Gagal melakukan deteksi otomatis' })
      } finally {
        isDetecting.value = false
      }
    }

    const validateAllFields = () => {
      validationErrors.value = {}
      let hasErrors = false

      // Check if at least one file is uploaded
      if (uploadedFiles.value.length === 0) {
        notification.error({
          message: 'Error',
          description: 'Mohon upload minimal satu dokumen surat',
        })
        return false
      }

      uploadedFiles.value.forEach((fileData, index) => {
        const errors = {}

        // Check required fields
        if (!fileData.waktuPenerimaan) {
          errors.waktuPenerimaan = 'Waktu penerimaan wajib diisi'
          hasErrors = true
        }
        if (!fileData.nomorSurat || fileData.nomorSurat.trim() === '') {
          errors.nomorSurat = 'Nomor surat wajib diisi'
          hasErrors = true
        }
        if (!fileData.tanggalSurat) {
          errors.tanggalSurat = 'Tanggal surat wajib diisi'
          hasErrors = true
        }
        if (!fileData.pengirim || fileData.pengirim.trim() === '') {
          errors.pengirim = 'Pengirim wajib diisi'
          hasErrors = true
        }
        if (!fileData.perihal || fileData.perihal.trim() === '') {
          errors.perihal = 'Perihal wajib diisi'
          hasErrors = true
        }
        if (!fileData.disposisi || fileData.disposisi.trim() === '') {
          errors.disposisi = 'Disposisi kepada wajib dipilih'
          hasErrors = true
        }

        // Validate conditional sections based on disposisi type
        if (fileData.disposisi === 'pekerja harian') {
          // Validate pekerja harian tasks
          if (!fileData.tasks || fileData.tasks.length === 0) {
            errors.tasks = 'Minimal harus ada satu task untuk pekerja harian'
            hasErrors = true
          } else {
            fileData.tasks.forEach((task, taskIndex) => {
              if (!task.disposisi || task.disposisi.length === 0) {
                errors[`task_${taskIndex}_disposisi`] = 'Disposisi kepada wajib dipilih'
                hasErrors = true
              }
              if (!task.judul || task.judul.trim() === '') {
                errors[`task_${taskIndex}_judul`] = 'Judul task wajib diisi'
                hasErrors = true
              }
            })
          }
        } else if (fileData.disposisi === 'pengurus') {
          // Validate pengurus dispositions
          if (!fileData.pengurusDispositions || fileData.pengurusDispositions.length === 0) {
            errors.pengurusDispositions = 'Minimal harus ada satu disposisi untuk pengurus'
            hasErrors = true
          } else {
            fileData.pengurusDispositions.forEach((pengurus, pengurusIndex) => {
              if (!pengurus.disposisi) {
                errors[`pengurus_${pengurusIndex}_disposisi`] = 'Disposisi kepada wajib dipilih'
                hasErrors = true
              }
              if (!pengurus.perihal || pengurus.perihal.trim() === '') {
                errors[`pengurus_${pengurusIndex}_perihal`] = 'Perihal instruksi wajib diisi'
                hasErrors = true
              }
            })
          }
        } else if (fileData.disposisi === 'penugasan dinas') {
          // Validate penugasan dinas
          const pd = fileData.penugasanDinas
          if (!pd.nomorSurat || pd.nomorSurat.trim() === '') {
            errors.pd_nomorSurat = 'Nomor surat tugas wajib diisi'
            hasErrors = true
          }
          if (!pd.sifatSurat || pd.sifatSurat.trim() === '') {
            errors.pd_sifatSurat = 'Sifat surat wajib dipilih'
            hasErrors = true
          }
          if (!pd.bentukSurat || pd.bentukSurat.trim() === '') {
            errors.pd_bentukSurat = 'Bentuk surat wajib dipilih'
            hasErrors = true
          }
          if (!pd.namaKegiatan || pd.namaKegiatan.trim() === '') {
            errors.pd_namaKegiatan = 'Nama kegiatan wajib diisi'
            hasErrors = true
          }
          if (!pd.waktuPerjalanan) {
            errors.pd_waktuPerjalanan = 'Waktu perjalanan wajib diisi'
            hasErrors = true
          }
          if (!pd.tempatKegiatan || pd.tempatKegiatan.trim() === '') {
            errors.pd_tempatKegiatan = 'Tempat kegiatan wajib diisi'
            hasErrors = true
          }
          if (!pd.penandatanganKiriId) {
            errors.pd_penandatanganKiri = 'Penandatangan kiri wajib diisi'
            hasErrors = true
          }
          if (!pd.keperluan || pd.keperluan.trim() === '') {
            errors.pd_keperluan = 'Keperluan wajib diisi'
            hasErrors = true
          }
          if (!pd.tanggalSurat) {
            errors.pd_tanggalSurat = 'Tanggal surat wajib diisi'
            hasErrors = true
          }
          if (
            !pd.perwakilan ||
            pd.perwakilan.length === 0 ||
            !pd.perwakilan.some(p => p.userId || p.isNonPengurus)
          ) {
            errors.pd_perwakilan = 'Minimal harus ada satu perwakilan dinas'
            hasErrors = true
          }
          if (
            pd.perwakilan.some(
              p =>
                p.isNonPengurus &&
                (!p.namaNonPengurus ||
                  !p.jabatanNonPengurus ||
                  !p.npaNonPengurus ||
                  p.namaNonPengurus.trim() === '' ||
                  p.jabatanNonPengurus.trim() === '' ||
                  p.npaNonPengurus.trim() === ''),
            )
          ) {
            errors.pd_perwakilan = 'Mohon isi Nama, Jabatan, dan NPA untuk setiap perwakilan dinas'
            hasErrors = true
          }
        }

        if (Object.keys(errors).length > 0) {
          validationErrors.value[index] = errors
        }
      })

      return !hasErrors
    }

    const handleSubmit = async () => {
      // Validate all required fields
      if (!validateAllFields()) {
        notification.error({
          message: 'Error',
          description: 'Mohon lengkapi semua field yang wajib diisi',
        })
        return
      }

      // Show confirmation modal
      submitConfirmModalVisible.value = true
    }

    const confirmSubmit = async () => {
      submitConfirmModalVisible.value = false
      submitting.value = true

      try {
        // Step 1: Upload all files first
        const uploadFormData = new FormData()
        uploadedFiles.value.forEach(fileData => {
          uploadFormData.append('files', fileData.file)
        })

        const uploadResponse = await uploadFilesAPI.uploadFiles(uploadFormData)

        if (!uploadResponse.data) {
          throw new Error('Gagal mengunggah dokumen')
        }

        // Step 2: For penugasan dinas files, create surat keluar FIRST and capture IDs
        const penugasanDinasFiles = uploadedFiles.value.filter(
          f => f.disposisi === 'penugasan dinas',
        )

        const suratKeluarIdMap = {} // Map to store file index -> suratKeluarId
        let suratKeluarSuccessCount = 0
        let suratKeluarErrors = []

        if (penugasanDinasFiles.length > 0) {
          for (const fileData of penugasanDinasFiles) {
            const pd = fileData.penugasanDinas
            const fileIndex = uploadedFiles.value.indexOf(fileData)

            // Prepare surat keluar data matching the create surat keluar API format
            const suratKeluarData = {
              jenisSurat: 'Surat Tugas',
              nomorSurat: pd.nomorSurat,
              sifatSurat: pd.sifatSurat,
              bentukSurat: pd.bentukSurat,
              suratUndanganAcaraId: pd.suratUndanganAcaraId || null,
              namaKegiatan: pd.namaKegiatan,
              waktuPerjalanan: formatDateTimeForAPI(pd.waktuPerjalanan),
              tempatKegiatan: pd.tempatKegiatan,
              tanggalSurat: formatForAPI(pd.tanggalSurat),
              penandatanganKiriId: pd.penandatanganKiriId,
              penandatanganKananId: pd.penandatanganKananId || null,
              keperluan: pd.keperluan,
              perwakilanDinas: pd.perwakilan.map(p => {
                if (p.isNonPengurus) {
                  return {
                    isNonPengurus: true,
                    namaNonPengurus: p.namaNonPengurus,
                    jabatanNonPengurus: p.jabatanNonPengurus,
                    npaNonPengurus: p.npaNonPengurus,
                  }
                } else {
                  return { userId: p.userId }
                }
              }),
              verifikatorIds: pd.verifikator.filter(v => v.userId).map(v => v.userId),
              isDraft: false,
            }

            try {
              const suratKeluarResponse = await suratKeluarAPI.create(suratKeluarData)
              const suratKeluarId = suratKeluarResponse.data?.data?.id
              if (suratKeluarId) {
                suratKeluarIdMap[fileIndex] = suratKeluarId
                suratKeluarSuccessCount++
              } else {
                const errorMsg = 'Tidak ada ID surat tugas dalam response'
                suratKeluarErrors.push(`Nomor ${pd.nomorSurat}: ${errorMsg}`)
              }
            } catch (error) {
              console.error('Error creating surat keluar:', error)
              const errorMsg = error.response?.data?.message || error.message
              suratKeluarErrors.push(`Nomor ${pd.nomorSurat}: ${errorMsg}`)
            }
          }
        }

        // Step 3: Build suratMasukList with uploaded file paths and suratKeluarId for penugasan dinas
        const suratMasukList = uploadedFiles.value.map((fileData, index) => {
          const uploadedFile = uploadResponse.data.data.uploaded[index]

          const suratItem = {
            pathToFile: uploadedFile.path,
            waktuPenerimaan: fileData.waktuPenerimaan
              ? dayjs(fileData.waktuPenerimaan).toISOString()
              : null,
            nomorSurat: fileData.nomorSurat,
            tanggalSurat: fileData.tanggalSurat ? dayjs(fileData.tanggalSurat).toISOString() : null,
            pengirim: fileData.pengirim,
            perihal: fileData.perihal,
            keterangan: fileData.keterangan || '',
            disposisi: fileData.disposisi,
            disposisiDetail: null,
            suratKeluarId: suratKeluarIdMap[index] || null,
          }

          // Add disposisiDetail based on disposisi type
          if (
            fileData.disposisi === 'pekerja harian' &&
            fileData.tasks &&
            fileData.tasks.length > 0
          ) {
            // Send all tasks as a list
            suratItem.disposisiDetail = fileData.tasks.map(task => ({
              disposisiKepada: task.disposisi || [],
              judulTask: task.judul,
              deadlineTask: task.deadline ? dayjs(task.deadline).toISOString() : null,
              deskripsiTask: task.deskripsi,
            }))
          } else if (
            fileData.disposisi === 'pengurus' &&
            fileData.pengurusDispositions &&
            fileData.pengurusDispositions.length > 0
          ) {
            // Send all pengurus dispositions as a list
            suratItem.disposisiDetail = fileData.pengurusDispositions.map(pengurus => ({
              disposisiKepada: pengurus.disposisi ? [pengurus.disposisi] : [],
              perihalInstruksi: pengurus.perihal,
              instruksiDisposisi: pengurus.instruksi,
            }))
          } else if (fileData.disposisi === 'penugasan dinas' && fileData.penugasanDinas) {
            // Handle penugasan dinas - include suratKeluarId from the created surat keluar
            suratItem.disposisiDetail = {
              nomorSurat: fileData.penugasanDinas.nomorSurat,
              sifatSurat: fileData.penugasanDinas.sifatSurat,
              bentukSurat: fileData.penugasanDinas.bentukSurat,
              namaKegiatan: fileData.penugasanDinas.namaKegiatan,
              waktuPerjalanan: formatDateTimeForAPI(fileData.penugasanDinas.waktuPerjalanan),
              tempatKegiatan: fileData.penugasanDinas.tempatKegiatan,
              penandatangan1: fileData.penugasanDinas.penandatangan1,
              penandatangan2: fileData.penugasanDinas.penandatangan2,
              keperluan: fileData.penugasanDinas.keperluan,
              perwakilan: fileData.penugasanDinas.perwakilan,
              verifikator: fileData.penugasanDinas.verifikator,
              suratKeluarId: suratKeluarIdMap[index] || null,
            }
          }

          return suratItem
        })

        const requestBody = {
          suratMasukList,
        }

        // Step 4: Call /surat-masuk to create surat masuk entries
        await suratMasukAPI.create(requestBody)

        // Show appropriate success/error messages
        if (penugasanDinasFiles.length > 0) {
          if (suratKeluarSuccessCount === penugasanDinasFiles.length) {
            notification.success({
              message: 'Success',
              description: `${uploadedFiles.value.length} surat masuk berhasil didaftarkan dan ${suratKeluarSuccessCount} surat tugas berhasil dibuat`,
            })
          } else if (suratKeluarSuccessCount > 0) {
            notification.warning({
              message: 'Warning',
              description: `${
                uploadedFiles.value.length
              } surat masuk berhasil didaftarkan, namun hanya ${suratKeluarSuccessCount} dari ${
                penugasanDinasFiles.length
              } surat tugas yang berhasil dibuat. ${suratKeluarErrors.join('; ')}`,
            })
          } else {
            notification.error({
              message: 'Error',
              description: `${
                uploadedFiles.value.length
              } surat masuk berhasil didaftarkan, tetapi gagal membuat surat tugas. ${suratKeluarErrors.join(
                '; ',
              )}`,
            })
          }
        } else {
          notification.success({
            message: 'Success',
            description: 'Surat masuk berhasil diregistrasikan.',
          })
        }

        router.push('/surat-masuk')
      } catch (error) {
        console.error('Error submitting form:', error)
        if (error.response?.data?.message) {
          notification.error({ message: 'Error', description: error.response.data.message })
        } else {
          notification.error({ message: 'Error', description: 'Gagal menyimpan data' })
        }
      } finally {
        submitting.value = false
      }
    }

    // Scroll handling functions
    const handleScroll = () => {
      showScrollTop.value = window.scrollY > scrollThreshold
    }

    const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    // Helper function to create preview URL for uploaded files
    const getFilePreviewUrl = file => {
      if (!file) return '#'
      return URL.createObjectURL(file)
    }

    return {
      formRef,
      fileInput,
      uploadedFiles,
      formData,
      submitting,
      isDetecting,
      validationErrors,
      triggerFileInput,
      handleFileChange,
      handleFileDrop,
      handleDragOver,
      handleDragLeave,
      openFile,
      removeFile,
      addTask,
      removeTask,
      addPengurusDisposition,
      removePengurusDisposition,
      addPerwakilanDinas,
      removePerwakilanDinas,
      handlePreviewPerwakilan,
      addVerifikator,
      removeVerifikator,
      previewSuratTugas,
      formatFileSize,
      autoDetectWithAI,
      handleSubmit,
      confirmSubmit,
      submitConfirmModalVisible,
      perwakilanColumns,
      verifikatorColumns,
      pengurusOptions,
      pekerjaHarianOptions,
      undanganAcaraOptions,
      getPengurusJabatan,
      getPengurusNPA,
      getPengurusName,
      filterPengurusOption,
      filterOption,
      disabledPastDates,
      incrementNomorSurat,
      decrementNomorSurat,
      canDecrementNomorSurat,
      // PDF Preview Modal
      pdfPreviewVisible,
      pdfPreviewTitle,
      pdfPreviewPath,
      showScrollTop,
      scrollToTop,
      getFilePreviewUrl,
    }
  },
}
</script>

<style lang="scss" scoped>
.registrasi-surat-masuk-page {
  min-height: calc(100vh - 64px);
  padding: 24px 24px 0px 24px;
  background: #fcfcfc;

  @media (max-width: 768px) {
    padding: 0;
  }

  .page-header {
    margin-bottom: 24px;

    h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #262626;
    }
  }

  .content-container {
    border-radius: 8px;
    padding: 24px;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      padding: 16px;
    }

    .file-hint {
      color: #8c8c8c;
      font-size: 13px;
      margin-bottom: 12px;
    }

    .upload-area {
      border: 2px dashed #d9d9d9;
      border-radius: 8px;
      padding: 48px 24px;
      text-align: center;
      cursor: pointer;
      transition: all 0.3s;

      &:hover,
      &.drag-over {
        border-color: #e10f28;
        background: #fdf4f5;
      }

      .upload-icon {
        margin-bottom: 16px;
        display: flex;
        justify-content: center;
      }

      .upload-text {
        font-size: 16px;
        color: #262626;
        margin-bottom: 8px;
      }

      .upload-hint {
        font-size: 14px;
        color: #8c8c8c;
        margin: 0;
      }
    }

    .uploaded-files-list {
      margin-top: 16px;
      border-radius: 6px;

      .uploaded-file-item {
        display: flex;
        align-items: center;
        padding: 4px 12px;
        background: white;
        border-radius: 4px;
        gap: 8px;

        &:hover {
          background: #fafafa;
        }

        &:last-child {
          margin-bottom: 0;
        }

        .file-icon {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.45);
        }

        .file-name {
          flex: 1;
          font-size: 14px;
          color: #321e8d;
          word-break: break-word;

          &:hover {
            text-decoration: underline;
          }
        }

        .delete-button {
          background: none;
          border: none;
          cursor: pointer;
          color: #ff4d4f;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 2px;
          transition: all 0.2s;

          &:hover {
            color: #ff7875;
            background: #fff1f0;
          }

          svg {
            width: 16px;
            height: 16px;
          }
        }
      }
    }

    .uploaded-surat-section {
      margin-top: 36px;

      .surat-header {
        margin-bottom: 16px;
        padding-bottom: 8px;
        border-bottom: 2px solid #f0f0f0;

        h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #262626;
        }
      }

      .file-attachment {
        display: flex;
        align-items: center;
        gap: 8px;
        border-radius: 4px;
        margin-bottom: 24px;

        .attachment-icon {
          font-size: 14px;
          color: rgba(0, 0, 0, 0.45);
        }

        .file-link {
          text-decoration: none;
          font-size: 14px;
          color: #321e8d;
          transition: color 0.2s;

          &:hover {
            text-decoration: underline;
          }
        }
      }

      .section-title {
        margin-top: 28px;
        margin-bottom: 16px;
        font-size: 16px;
        font-weight: 600;
        color: #262626;
      }

      :deep(.ant-radio-wrapper) {
        display: block;
        margin-bottom: 12px;
        font-size: 14px;

        .ant-radio {
          margin-right: 8px;
        }
      }
    }

    .uploaded-file {
      margin-top: 16px;
      padding: 12px 16px;
      background: #f5f5f5;
      border-radius: 4px;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .file-info {
        display: flex;
        align-items: center;
        gap: 8px;

        .file-icon {
          font-size: 20px;
          color: #1890ff;
        }

        .file-name {
          font-weight: 500;
          color: #262626;
        }

        .file-size {
          color: #8c8c8c;
          font-size: 13px;
        }
      }
    }

    .auto-detect-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .auto-detect-button {
      background: white;
      border: 2px solid #e10f28;
      color: #e10f28;
      border-radius: 50px;
      padding: 8px 24px;
      height: auto;
      font-weight: 700;
      font-size: 14px;
      font-family: 'Barlow' !important;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: all 0.3s;

      svg {
        flex-shrink: 0;
      }

      &:hover:not(:disabled) {
        background: #fff1f0;
        border-color: #e10f28;
        color: #e10f28;
      }

      &:disabled {
        border-color: #d9d9d9;
        color: #d9d9d9;
        cursor: not-allowed;
      }
    }

    .info-icon {
      margin-left: 8px;
      color: #8c8c8c;
      cursor: default;
    }

    .hapus-surat-button {
      width: 100%;
      max-width: 180px;
      background: #1a1a1a;
      border: 2px solid #1a1a1a;
      border-radius: 50px;
      padding: 6px 24px;
      font-size: 14px;
      font-weight: 700;
      font-family: 'Barlow' !important;
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      cursor: pointer;
      transition: all 0.3s;

      svg {
        flex-shrink: 0;

        path {
          transition: stroke 0.3s;
        }
      }

      &:hover {
        background: white;
        border-color: #1a1a1a;
        color: #1a1a1a;

        svg path {
          stroke: #1a1a1a;
        }
      }

      &:active {
        transform: translateY(0);
      }
    }

    .task-cards-section {
      border-radius: 8px;

      .task-info-text {
        color: #8c8c8c;
        font-size: 13px;
        margin-bottom: 16px;
        line-height: 1.6;
      }

      .task-card {
        background: white;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
        border: 1px solid #f0f0f0;

        @media (max-width: 768px) {
          padding: 12px;
        }

        .hapus-task-button {
          background: #1a1a1a;
          border: 2px solid #1a1a1a;
          border-radius: 50px;
          padding: 6px 24px;
          margin-top: 4px;
          font-size: 14px;
          font-weight: 700;
          font-family: 'Barlow' !important;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s;

          svg {
            flex-shrink: 0;

            path {
              transition: stroke 0.3s;
            }
          }

          &:hover {
            background: white;
            border-color: #1a1a1a;
            color: #1a1a1a;

            svg path {
              stroke: #1a1a1a;
            }
          }

          &:active {
            transform: translateY(0);
          }
        }
      }

      .tambah-task-button {
        color: #262626;
        margin-bottom: 16px;
        font-weight: 600;
        font-size: 14px;
        padding: 0;
        height: auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }

    .pengurus-cards-section {
      border-radius: 8px;

      .pengurus-info-text {
        color: #8c8c8c;
        font-size: 13px;
        margin-bottom: 16px;
        line-height: 1.6;
      }

      .pengurus-card {
        background: white;
        border-radius: 8px;
        padding: 20px;
        margin-bottom: 20px;
        border: 1px solid #f0f0f0;

        @media (max-width: 768px) {
          padding: 12px;
        }

        .hapus-disposisi-button {
          background: #1a1a1a;
          border: 2px solid #1a1a1a;
          border-radius: 50px;
          padding: 6px 24px;
          margin-top: 4px;
          font-size: 14px;
          font-weight: 700;
          font-family: 'Barlow' !important;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          cursor: pointer;
          transition: all 0.3s;

          svg {
            flex-shrink: 0;

            path {
              transition: stroke 0.3s;
            }
          }

          &:hover {
            background: white;
            border-color: #1a1a1a;
            color: #1a1a1a;

            svg path {
              stroke: #1a1a1a;
            }
          }

          &:active {
            transform: translateY(0);
          }
        }
      }

      .tambah-disposisi-button {
        color: #262626;
        font-weight: 700;
        margin-bottom: 16px;
        font-size: 14px;
        padding: 0;
        height: auto;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }
    }

    .penugasan-dinas-section {
      border-radius: 8px;

      .penugasan-info-text {
        color: #8c8c8c;
        font-size: 13px;
        margin-bottom: 24px;
        line-height: 1.6;
      }

      .nomor-surat-group {
        margin-bottom: 24px;
        position: relative;

        .nomor-surat-input {
          padding-right: 50px;
        }

        :deep(.ant-input-suffix) {
          display: flex;
          gap: 8px;
          align-items: center;
        }
      }

      .bentuk-surat-option {
        margin-bottom: 16px;

        .bentuk-surat-desc {
          color: #8c8c8c;
          font-size: 12px;
          margin: 8px 0 0 24px;
          line-height: 1.5;
        }
      }

      .perwakilan-table,
      .verifikator-table {
        margin: 16px 0;
        background: white;

        :deep(.ant-table) {
          font-size: 13px;
        }

        :deep(.ant-table-thead > tr > th) {
          background: #fafafa;
          font-weight: 600;
          border-bottom: 1px solid #f0f0f0;
        }

        :deep(.ant-table-tbody > tr > td) {
          border-bottom: 1px solid #f0f0f0;
          padding: 12px 16px;
        }

        :deep(.ant-table-tbody > tr:hover > td) {
          background: #f5f5f5;
        }
      }

      .preview-button {
        background: white;
        border: 2px solid #e10f28;
        color: #e10f28;
        border-radius: 50px;
        padding: 12px 24px;
        height: auto;
        font-weight: 600;
        font-size: 14px;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 32px;
        transition: all 0.3s;
        cursor: pointer;

        svg {
          flex-shrink: 0;
        }

        &:hover {
          background: #fff1f0;
          border-color: #e10f28;
          color: #e10f28;
        }
      }

      .verifikator-info {
        color: #8c8c8c;
        font-size: 13px;
        margin: 24px 0 16px 0;
        line-height: 1.6;
      }

      // Nomor Surat Input Styles (matching NomorSuratInput component)
      .nomor-surat-input {
        margin-bottom: 24px;

        .section-label {
          display: block;
          font-weight: 500;
          color: #262626;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .section-description {
          font-size: 12px;
          color: #8c8c8c;
          margin-bottom: 8px;
          line-height: 1.5;
        }

        .input-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;

          .nomor-input {
            flex: 1;
          }

          .btn-action {
            width: 40px;
            height: 40px;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 6px;
            background: white;

            &:hover:not(:disabled) {
              border-color: #e10f28;
              color: #e10f28;
            }

            &:disabled {
              color: #d9d9d9;
              cursor: not-allowed;
              border-color: #f0f0f0;
              background: #fafafa;
            }
          }
        }

        .error-hint {
          display: block;
          color: #ff4d4f;
          font-size: 12px;
          margin-top: 4px;
        }
      }

      .alur-verifikasi-section {
        margin-top: 32px;

        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #262626;
          margin-bottom: 16px;
        }

        .alur-flow {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: center;

          .alur-item {
            display: flex;
            align-items: center;
            gap: 16px;
          }

          .alur-box {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 16px;
            background: #f5f5f5;
            border: 1px solid #d9d9d9;
            border-radius: 8px;
            min-width: 120px;

            .alur-number {
              width: 32px;
              height: 32px;
              border-radius: 50%;
              background: #e10f28;
              color: white;
              display: flex;
              align-items: center;
              justify-content: center;
              font-weight: 600;
              font-size: 14px;
            }

            .alur-name {
              font-size: 12px;
              color: #262626;
              text-align: center;
              font-weight: 500;
            }
          }

          .alur-arrow {
            display: flex;
            align-items: center;
          }
        }
      }
    }

    :deep(.ant-form-item-label > label) {
      font-weight: 600;
      color: #262626;
    }
    :deep(.ant-input::placeholder),
    :deep(.ant-input-number::placeholder),
    :deep(.ant-picker::placeholder),
    :deep(.ant-select-selector::placeholder),
    :deep(.ant-select-selection-placeholder),
    :deep(.ant-textarea::placeholder) {
      color: #bfbfbf !important;
      opacity: 1 !important;
      font-size: 14px;
    }

    :deep(.ant-select-selection-item) {
      font-size: 14px !important;
    }

    :deep(.ant-input-placeholder),
    :deep(.ant-textarea-placeholder) {
      color: #bfbfbf !important;
    }
  }
}

.loading-modal {
  text-align: center;
  padding: 40px 60px;

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #f0f0f0;
    border-top: 4px solid #e10f28;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 24px;
  }

  .loading-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 8px 0;
  }

  .loading-text {
    font-size: 14px;
    color: #8c8c8c;
    margin: 0 auto;
    max-width: 300px;
    line-height: 1.6;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.confirm-button {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28;
  width: 50%;
  max-width: 150px;
  background: #e10f28;
  color: white;
  padding: 6px 12px;
  transition: all 0.3s;
  height: auto;
  line-height: 1.5;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: 'Barlow' !important;

  @media (max-width: 768px) {
    width: 100%;
  }

  &:hover:not(:disabled) {
    background: white;
    border-color: #e10f28;
    color: #e10f28;
  }

  &:disabled {
    border-color: #98a2b3;
    background: #98a2b3;
    color: white;
    cursor: not-allowed;
    opacity: 0.8;
  }

  .button-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #ffffff;
    border-top: 2px solid transparent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
}

::v-deep(.ant-btn) {
  display: flex;
  align-items: center;
  gap: 8px;
}

::v-deep(.ant-radio-group) {
  display: flex;
}

::v-deep(label.ant-form-item-required) {
  margin-bottom: 0 !important;
  font-weight: 600;
}

::v-deep(.ant-input-lg) {
  font-size: 14px;
}

::v-deep(.ant-radio-group) {
  display: flex;

  @media (max-width: 714px) {
    flex-direction: column;
  }
}

// Scroll to Top Button
.scroll-to-top-btn {
  position: fixed;
  bottom: 32px;
  right: 32px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #7c3aed;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;

  &:hover {
    background: #6d28d9;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
}

// Fade transition for scroll button
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.font-button {
  font-family: 'Barlow' !important;
  font-size: 14px;
  font-weight: 700;
}

// Submit Confirmation Modal Styles
.submit-confirm-modal {
  .confirm-modal-content {
    .confirm-modal-title {
      font-size: 18px;
      font-weight: 600;
      color: #262626;
      margin-bottom: 16px;
    }

    .confirm-modal-description {
      font-size: 14px;
      color: #595959;
      margin-bottom: 24px;
      line-height: 1.6;
    }

    .confirm-modal-actions {
      display: flex;
      gap: 12px;
      justify-content: center;

      .modal-cancel-button {
        width: 50%;
        max-width: 230px;
        background: #ffffff;
        border: 2px solid #1a1a1a;
        color: #1a1a1a;
        border-radius: 50px;
        padding: 6px 24px;
        font-size: 14px;
        font-weight: 700;
        font-family: 'Barlow' !important;
        cursor: pointer;
        transition: all 0.3s;

        &:hover {
          background: #1a1a1a;
          color: white;
        }
      }

      .modal-confirm-button {
        width: 50%;
        max-width: 200px;
        border-radius: 50px;
        font-size: 14px;
        font-weight: 700;
        font-family: 'Barlow' !important;
        border: 2px solid #e10f28;
        background: #e10f28;
        color: white;
        padding: 6px 12px;
        cursor: pointer;
        transition: all 0.3s;

        &:hover:not(:disabled) {
          background: white;
          border-color: #e10f28;
          color: #e10f28;
        }

        &:disabled {
          border-color: #98a2b3;
          background: #98a2b3;
          color: white;
          cursor: not-allowed;
          opacity: 0.8;
        }
      }
    }
  }
}

/* 1. The border color when selected */
:deep(.ant-radio-checked .ant-radio-inner) {
  border-color: #e10f28 !important;
}

/* 2. The inner dot color when selected */
:deep(.ant-radio-checked .ant-radio-inner::after) {
  background-color: #e10f28 !important;
}

/* 3. The border color on hover */
:deep(.ant-radio-wrapper:hover .ant-radio .ant-radio-inner),
:deep(.ant-radio:hover .ant-radio-inner) {
  border-color: #e10f28 !important;
}

/* 4. (Optional) The focus glow ring/shadow */
:deep(.ant-radio-input:focus + .ant-radio-inner) {
  box-shadow: 0 0 0 3px rgba(225, 15, 40, 0.2);
}
</style>
